import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { FloppyDisk, TestTube, Lock } from "@phosphor-icons/react";

function Section({ title, description, children }) {
  return (
    <div className="seva-card">
      <h2 className="font-display font-bold text-xl">{title}</h2>
      {description && <p className="text-sm text-[#56624F] mt-1 mb-5">{description}</p>}
      <div className="space-y-4">{children}</div>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <label className="block">
      <div className="text-sm font-semibold">{label}</div>
      {hint && <div className="text-xs text-[#56624F] mb-1.5">{hint}</div>}
      {children}
    </label>
  );
}

function Toggle({ checked, onChange, testid }) {
  return (
    <button type="button" role="switch" aria-checked={checked} onClick={() => onChange(!checked)} data-testid={testid}
      className={"w-11 h-6 rounded-full relative transition-colors " + (checked ? "bg-[#2F7A4A]" : "bg-[#DCE5D4]")}>
      <span className={"absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-all " + (checked ? "left-5" : "left-0.5")} />
    </button>
  );
}

function GatewaySection({ name, title }) {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get(`/admin/settings/gateway/${name}`).then((r) => setCfg(r.data)); }, [name]);
  if (!cfg) return null;
  const patch = (k, v) => setCfg((c) => ({ ...c, [k]: v }));
  const save = async () => {
    setSaving(true);
    try {
      await api.post("/admin/settings/gateway", { ...cfg, gateway: name });
      toast.success(`${title} saved`);
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };
  return (
    <Section title={title} description={`Store API credentials for both Test and Live environments. Switch between them using the mode toggle. Both gateways can be enabled at once — monthly donations always use Razorpay, one-time donations use Cashfree when enabled (otherwise Razorpay).`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Toggle checked={cfg.enabled} onChange={(v) => patch("enabled", v)} testid={`${name}-enabled`} />
          <span className="text-sm font-semibold">{cfg.enabled ? "Enabled" : "Disabled"}</span>
        </div>
        <div className="inline-flex rounded-full border border-[#DCE5D4] p-1" data-testid={`${name}-mode`}>
          {["test", "live"].map((m) => (
            <button key={m} onClick={() => patch("mode", m)} className={"px-4 py-1.5 text-sm rounded-full font-semibold " + (cfg.mode === m ? "bg-[#4FAC39] text-white" : "text-[#56624F]")}>
              {m === "test" ? "Test" : "Live"}
            </button>
          ))}
        </div>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <Field label="Test Key ID / App ID">
          <input value={cfg.test_key_id || ""} onChange={(e) => patch("test_key_id", e.target.value)} className="w-full px-4 py-2.5 rounded-xl border border-[#DCE5D4] bg-white" data-testid={`${name}-test-key-id`} />
        </Field>
        <Field label="Test Key Secret">
          <input value={cfg.test_key_secret || ""} onChange={(e) => patch("test_key_secret", e.target.value)} type="password" className="w-full px-4 py-2.5 rounded-xl border border-[#DCE5D4] bg-white" data-testid={`${name}-test-key-secret`} />
        </Field>
        <Field label="Live Key ID / App ID">
          <input value={cfg.live_key_id || ""} onChange={(e) => patch("live_key_id", e.target.value)} className="w-full px-4 py-2.5 rounded-xl border border-[#DCE5D4] bg-white" data-testid={`${name}-live-key-id`} />
        </Field>
        <Field label="Live Key Secret">
          <input value={cfg.live_key_secret || ""} onChange={(e) => patch("live_key_secret", e.target.value)} type="password" className="w-full px-4 py-2.5 rounded-xl border border-[#DCE5D4] bg-white" data-testid={`${name}-live-key-secret`} />
        </Field>
        <div className="md:col-span-2">
          <Field label="Webhook Secret (optional)" hint="Used to verify incoming webhooks from the gateway.">
            <input value={cfg.webhook_secret || ""} onChange={(e) => patch("webhook_secret", e.target.value)} type="password" className="w-full px-4 py-2.5 rounded-xl border border-[#DCE5D4] bg-white" />
          </Field>
        </div>
      </div>
      <button onClick={save} disabled={saving} className="btn-primary inline-flex items-center gap-2" data-testid={`${name}-save`}>
        <FloppyDisk size={16} /> {saving ? "Saving..." : `Save ${title}`}
      </button>
    </Section>
  );
}

function SMTPSection() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  useEffect(() => { api.get("/admin/settings/smtp").then((r) => setCfg(r.data)); }, []);
  if (!cfg) return null;
  const patch = (k, v) => setCfg((c) => ({ ...c, [k]: v }));
  const save = async () => {
    setSaving(true);
    try {
      const { smtp_password_set, ...payload } = cfg;
      await api.post("/admin/settings/smtp", payload);
      toast.success("SMTP saved");
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };
  const sendTest = async () => {
    setTesting(true);
    try {
      const to = window.prompt("Send test email to:", "");
      if (!to) return;
      await api.post("/admin/settings/smtp/test", { to });
      toast.success("Test email sent");
    } catch (e) { toast.error(e?.response?.data?.detail || "Test failed"); }
    finally { setTesting(false); }
  };
  return (
    <Section title="SMTP (Hostinger)" description="Automated donation receipt emails. Defaults to Hostinger: smtp.hostinger.com port 465 SSL.">
      <div className="flex items-center gap-3">
        <Toggle checked={cfg.enabled} onChange={(v) => patch("enabled", v)} testid="smtp-enabled" />
        <span className="text-sm font-semibold">{cfg.enabled ? "Sending enabled" : "Sending disabled"}</span>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <Field label="SMTP Host">
          <input value={cfg.smtp_host} onChange={(e) => patch("smtp_host", e.target.value)} className="w-full px-4 py-2.5 rounded-xl border border-[#DCE5D4] bg-white" data-testid="smtp-host" />
        </Field>
        <Field label="SMTP Port" hint="465 (SSL) or 587 (STARTTLS)">
          <input type="number" value={cfg.smtp_port} onChange={(e) => patch("smtp_port", Number(e.target.value))} className="w-full px-4 py-2.5 rounded-xl border border-[#DCE5D4] bg-white" data-testid="smtp-port" />
        </Field>
        <Field label="Username (your email@yourdomain)">
          <input value={cfg.smtp_username} onChange={(e) => patch("smtp_username", e.target.value)} className="w-full px-4 py-2.5 rounded-xl border border-[#DCE5D4] bg-white" data-testid="smtp-username" />
        </Field>
        <Field label="Password">
          <input type="password" value={cfg.smtp_password || ""} onChange={(e) => patch("smtp_password", e.target.value)} className="w-full px-4 py-2.5 rounded-xl border border-[#DCE5D4] bg-white" data-testid="smtp-password" />
        </Field>
        <Field label="From Email">
          <input value={cfg.from_email} onChange={(e) => patch("from_email", e.target.value)} className="w-full px-4 py-2.5 rounded-xl border border-[#DCE5D4] bg-white" data-testid="smtp-from-email" />
        </Field>
        <Field label="From Name">
          <input value={cfg.from_name} onChange={(e) => patch("from_name", e.target.value)} className="w-full px-4 py-2.5 rounded-xl border border-[#DCE5D4] bg-white" data-testid="smtp-from-name" />
        </Field>
      </div>
      <div className="flex gap-2">
        <button onClick={save} disabled={saving} className="btn-primary inline-flex items-center gap-2" data-testid="smtp-save"><FloppyDisk size={16} /> {saving ? "Saving..." : "Save SMTP"}</button>
        <button onClick={sendTest} disabled={testing} className="btn-secondary inline-flex items-center gap-2" data-testid="smtp-test"><TestTube size={16} /> {testing ? "Sending..." : "Send Test"}</button>
      </div>
    </Section>
  );
}

function BrandingSection() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/admin/settings/branding").then((r) => setCfg(r.data)); }, []);
  if (!cfg) return null;
  const patch = (k, v) => setCfg((c) => ({ ...c, [k]: v }));
  const uploadLogo = async (file) => {
    try {
      const fd = new FormData(); fd.append("file", file);
      const up = await api.post("/admin/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      patch("email_logo_url", up.data.url);
      toast.success("Logo uploaded");
    } catch { toast.error("Upload failed"); }
  };
  const save = async () => {
    setSaving(true);
    try { await api.post("/admin/settings/branding", cfg); toast.success("Branding saved"); }
    catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };
  return (
    <Section title="Site Branding & Email Logo" description="Controls the header title/subtitle shown across the public site, the Instagram handle, and the logo image shown in every donation & OTP email.">
      <div className="grid md:grid-cols-2 gap-4">
        <Field label="Header Title">
          <input value={cfg.header_title || ""} onChange={(e) => patch("header_title", e.target.value)} className="w-full px-4 py-2.5 rounded-xl border border-[#DCE5D4] bg-white" data-testid="branding-header-title" />
        </Field>
        <Field label="Header Subtitle">
          <input value={cfg.header_subtitle || ""} onChange={(e) => patch("header_subtitle", e.target.value)} className="w-full px-4 py-2.5 rounded-xl border border-[#DCE5D4] bg-white" data-testid="branding-header-subtitle" />
        </Field>
        <Field label="Instagram Handle" hint="Without '@'. Shown as 'Follow us on Instagram @handle'.">
          <input value={cfg.instagram_handle || ""} onChange={(e) => patch("instagram_handle", e.target.value.replace(/^@/, ""))} className="w-full px-4 py-2.5 rounded-xl border border-[#DCE5D4] bg-white" data-testid="branding-instagram" />
        </Field>
        <Field label="Email Display Picture">
          <div className="flex items-center gap-3">
            {cfg.email_logo_url && <img src={cfg.email_logo_url} alt="" className="w-14 h-14 rounded-full border border-[#DCE5D4] object-cover bg-white" />}
            <label className="btn-secondary cursor-pointer" data-testid="branding-logo-upload"><input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && uploadLogo(e.target.files[0])} />Upload</label>
            {cfg.email_logo_url && <button type="button" onClick={() => patch("email_logo_url", "")} className="text-sm text-[#B84A34]">Remove</button>}
          </div>
        </Field>
      </div>
      <div className="pt-4 mt-4 border-t border-[#DCE5D4]">
        <h3 className="font-display font-bold text-lg mb-1">Receipt & Tax Info</h3>
        <p className="text-sm text-[#56624F] mb-4">These values appear on both the Acknowledgement and 80G Tax Exempt PDF receipts sent to donors.</p>
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Organisation Short Name" hint="e.g. SEVA — used in receipt title & signature block.">
            <input value={cfg.org_short_name || ""} onChange={(e) => patch("org_short_name", e.target.value)} className="w-full px-4 py-2.5 rounded-xl border border-[#DCE5D4] bg-white" data-testid="branding-org-short-name" />
          </Field>
          <Field label="Organisation Full Name" hint="e.g. SUPPORTIVE EFFORTS FOR VOICELESS ANIMALS">
            <input value={cfg.org_full_name || ""} onChange={(e) => patch("org_full_name", e.target.value)} className="w-full px-4 py-2.5 rounded-xl border border-[#DCE5D4] bg-white" data-testid="branding-org-full-name" />
          </Field>
          <Field label="Organisation PAN" hint="Shown in the 80G receipt.">
            <input value={cfg.org_pan || ""} onChange={(e) => patch("org_pan", e.target.value.toUpperCase())} className="w-full px-4 py-2.5 rounded-xl border border-[#DCE5D4] bg-white uppercase" data-testid="branding-org-pan" />
          </Field>
          <Field label="80G Registration Number" hint="Unique 80G registration reference (e.g. AABCS1234KF20214).">
            <input value={cfg.org_80g_reg || ""} onChange={(e) => patch("org_80g_reg", e.target.value)} className="w-full px-4 py-2.5 rounded-xl border border-[#DCE5D4] bg-white" data-testid="branding-org-80g-reg" />
          </Field>
          <Field label="Trust / Society Reg. No." hint="Printed under the signatory block on 80G receipts.">
            <input value={cfg.org_reg_no || ""} onChange={(e) => patch("org_reg_no", e.target.value)} className="w-full px-4 py-2.5 rounded-xl border border-[#DCE5D4] bg-white" data-testid="branding-org-reg-no" />
          </Field>
          <Field label="Signatory Name">
            <input value={cfg.signatory_name || ""} onChange={(e) => patch("signatory_name", e.target.value)} className="w-full px-4 py-2.5 rounded-xl border border-[#DCE5D4] bg-white" data-testid="branding-signatory-name" />
          </Field>
          <Field label="Signatory Title" hint="e.g. President / Secretary">
            <input value={cfg.signatory_title || ""} onChange={(e) => patch("signatory_title", e.target.value)} className="w-full px-4 py-2.5 rounded-xl border border-[#DCE5D4] bg-white" data-testid="branding-signatory-title" />
          </Field>
          <Field label="Signature Image" hint="Scanned handwritten signature — shown above the signatory name on the 80G PDF.">
            <div className="flex items-center gap-3">
              {cfg.signature_image_url && <img src={cfg.signature_image_url} alt="signature" className="h-12 rounded-md border border-[#DCE5D4] bg-white px-2" />}
              <label className="btn-secondary cursor-pointer !py-2 !px-4 text-sm" data-testid="branding-signature-upload">
                <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
                  const file = e.target.files[0]; if (!file) return;
                  try {
                    const fd = new FormData(); fd.append("file", file);
                    const up = await api.post("/admin/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
                    patch("signature_image_url", up.data.url);
                    toast.success("Signature uploaded");
                  } catch { toast.error("Upload failed"); }
                }} />Upload
              </label>
              {cfg.signature_image_url && <button type="button" onClick={() => patch("signature_image_url", "")} className="text-sm text-[#B84A34]">Remove</button>}
            </div>
          </Field>
          <Field label="Website (shown on PDF)">
            <input value={cfg.org_website || ""} onChange={(e) => patch("org_website", e.target.value)} className="w-full px-4 py-2.5 rounded-xl border border-[#DCE5D4] bg-white" data-testid="branding-org-website" />
          </Field>
          <Field label="Contact Email (shown on PDF)">
            <input value={cfg.org_email || ""} onChange={(e) => patch("org_email", e.target.value)} className="w-full px-4 py-2.5 rounded-xl border border-[#DCE5D4] bg-white" data-testid="branding-org-email" />
          </Field>
          <Field label="Contact Phone (shown on PDF)">
            <input value={cfg.org_phone || ""} onChange={(e) => patch("org_phone", e.target.value)} className="w-full px-4 py-2.5 rounded-xl border border-[#DCE5D4] bg-white" data-testid="branding-org-phone" />
          </Field>
        </div>
      </div>
      <div className="pt-4 mt-4 border-t border-[#DCE5D4]">
        <h3 className="font-display font-bold text-lg mb-1">Exit-Intent &ldquo;Please don&apos;t go&rdquo; Popup</h3>
        <p className="text-sm text-[#56624F] mb-4">Shown site-wide when a visitor tries to close the donation amount popup. Customize the image, headline, and message to match the season/campaign tone.</p>
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Popup Image" hint="Square illustration or photo (e.g. your &lsquo;dog-in-jar&rsquo; graphic). Recommended: 400×400px PNG/JPG.">
            <div className="flex items-center gap-3">
              {cfg.exit_popup_image_url && <img src={cfg.exit_popup_image_url} alt="exit popup" className="h-20 w-20 rounded-xl border border-[#DCE5D4] bg-white object-contain" />}
              <label className="btn-secondary cursor-pointer !py-2 !px-4 text-sm" data-testid="branding-exit-popup-upload">
                <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
                  const file = e.target.files[0]; if (!file) return;
                  try {
                    const fd = new FormData(); fd.append("file", file);
                    const up = await api.post("/admin/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
                    patch("exit_popup_image_url", up.data.url);
                    toast.success("Popup image uploaded");
                  } catch { toast.error("Upload failed"); }
                }} />Upload
              </label>
              {cfg.exit_popup_image_url && <button type="button" onClick={() => patch("exit_popup_image_url", "")} className="text-sm text-[#B84A34]">Remove</button>}
            </div>
          </Field>
          <Field label="Popup Headline">
            <input value={cfg.exit_popup_title || ""} onChange={(e) => patch("exit_popup_title", e.target.value)} placeholder="Please don't go yet" className="w-full px-4 py-2.5 rounded-xl border border-[#DCE5D4] bg-white" data-testid="branding-exit-popup-title" />
          </Field>
          <Field label="Popup Sub-text" hint="Shown below the headline. One short paragraph.">
            <textarea rows={3} value={cfg.exit_popup_subtext || ""} onChange={(e) => patch("exit_popup_subtext", e.target.value)} className="w-full px-4 py-2.5 rounded-xl border border-[#DCE5D4] bg-white md:col-span-2" data-testid="branding-exit-popup-subtext" />
          </Field>
        </div>
      </div>
      <button onClick={save} disabled={saving} className="btn-primary inline-flex items-center gap-2" data-testid="branding-save"><FloppyDisk size={16} /> {saving ? "Saving..." : "Save Branding"}</button>
    </Section>
  );
}

function MetaSection() {
  const [cfg, setCfg] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [campaignsError, setCampaignsError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  useEffect(() => {
    api.get("/admin/settings/meta").then((r) => setCfg(r.data));
    api.get("/admin/meta-ads/campaigns").then((r) => {
      setCampaigns(r.data?.items || []);
      setCampaignsError(r.data?.error || null);
    }).catch(() => {});
  }, []);
  if (!cfg) return null;
  const patch = (k, v) => setCfg((c) => ({ ...c, [k]: v }));
  const toggleId = (id) => {
    const set = new Set(cfg.campaign_filter_ids || []);
    if (set.has(id)) set.delete(id); else set.add(id);
    patch("campaign_filter_ids", Array.from(set));
  };
  const save = async () => {
    setSaving(true);
    try { await api.post("/admin/settings/meta", cfg); toast.success("Meta settings saved"); }
    catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };
  const sendTestEvent = async () => {
    setTesting(true);
    try {
      await api.post("/admin/settings/meta/test");
      toast.success("Test event accepted by Meta. Check Events Manager → Test Events (or Overview, may take a few minutes).");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Meta CAPI test failed");
    } finally {
      setTesting(false);
    }
  };
  return (
    <Section title="Meta Pixel, CAPI & Ads" description="Pixel fires on the landing page. Conversions API sends server-side events (deduped). Marketing API fetches ad spend for ROAS on the dashboard.">
      <div className="flex items-center gap-3">
        <Toggle checked={cfg.enabled} onChange={(v) => patch("enabled", v)} testid="meta-enabled" />
        <span className="text-sm font-semibold">{cfg.enabled ? "Enabled" : "Disabled"}</span>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <Field label="Pixel ID"><input value={cfg.pixel_id} onChange={(e) => patch("pixel_id", e.target.value)} className="w-full px-4 py-2.5 rounded-xl border border-[#DCE5D4] bg-white" data-testid="meta-pixel-id" /></Field>
        <Field label="Meta Ad Account ID" hint="With or without 'act_' prefix, e.g. act_1234567890"><input value={cfg.ad_account_id || ""} onChange={(e) => patch("ad_account_id", e.target.value)} className="w-full px-4 py-2.5 rounded-xl border border-[#DCE5D4] bg-white" data-testid="meta-ad-account-id" /></Field>
        <Field label="CAPI Access Token"><input type="password" value={cfg.capi_access_token} onChange={(e) => patch("capi_access_token", e.target.value)} className="w-full px-4 py-2.5 rounded-xl border border-[#DCE5D4] bg-white" data-testid="meta-capi-token" /></Field>
        <Field label="Marketing API Token (optional)" hint="Needs ads_read permission. If blank, the CAPI token will be tried."><input type="password" value={cfg.marketing_access_token || ""} onChange={(e) => patch("marketing_access_token", e.target.value)} className="w-full px-4 py-2.5 rounded-xl border border-[#DCE5D4] bg-white" data-testid="meta-mkt-token" /></Field>
        <Field label="GST % on Ad Spend"><input type="number" step="0.01" value={cfg.gst_percent ?? 18} onChange={(e) => patch("gst_percent", Number(e.target.value))} className="w-full px-4 py-2.5 rounded-xl border border-[#DCE5D4] bg-white" data-testid="meta-gst" /></Field>
        <Field label="Test Event Code (optional)"><input value={cfg.test_event_code || ""} onChange={(e) => patch("test_event_code", e.target.value)} className="w-full px-4 py-2.5 rounded-xl border border-[#DCE5D4] bg-white" /></Field>
      </div>

      {/* Campaign include/exclude */}
      <div className="pt-2 border-t border-[#DCE5D4]">
        <div className="text-sm font-semibold mb-1">Which Meta ad campaigns should count?</div>
        <div className="text-xs text-[#56624F] mb-3">This controls which ad campaigns are counted on the dashboard ROAS/Spend/Profit cards. Unrelated ad accounts or campaigns won&rsquo;t skew your numbers.</div>
        <div className="inline-flex rounded-full border border-[#DCE5D4] p-1 mb-3 flex-wrap" data-testid="meta-filter-mode">
          {[
            { k: "all", l: "All campaigns" },
            { k: "include", l: "Only selected" },
            { k: "exclude", l: "Exclude selected" },
          ].map((m) => (
            <button key={m.k} type="button" onClick={() => patch("campaign_filter_mode", m.k)} className={"px-3 py-1.5 text-xs rounded-full font-semibold " + ((cfg.campaign_filter_mode || "all") === m.k ? "bg-[#4FAC39] text-white" : "text-[#56624F]")}>{m.l}</button>
          ))}
        </div>
        {campaignsError && (
          <div className="text-xs bg-[#FDEAE5] border border-[#E4A8A0] rounded-xl p-3 text-[#7A3428] mb-3" data-testid="meta-campaigns-error">
            Couldn&rsquo;t load Meta ad campaigns: {campaignsError}
          </div>
        )}
        {(cfg.campaign_filter_mode === "include" || cfg.campaign_filter_mode === "exclude") && (
          <div className="max-h-64 overflow-y-auto border border-[#DCE5D4] rounded-xl p-2">
            {campaigns.length === 0 && <div className="text-sm text-[#56624F] p-2">No campaigns loaded (check ad account + token).</div>}
            {campaigns.map((c) => (
              <label key={c.id} className="flex items-center gap-2 px-2 py-1 hover:bg-[#EEF4E9] rounded cursor-pointer text-sm" data-testid={`meta-campaign-${c.id}`}>
                <input type="checkbox" checked={(cfg.campaign_filter_ids || []).includes(c.id)} onChange={() => toggleId(c.id)} />
                <span className="flex-1 truncate">{c.name}</span>
                <span className="text-xs text-[#56624F]">{c.status}</span>
              </label>
            ))}
          </div>
        )}
      </div>

      <div className="flex items-center gap-3">
        <button onClick={save} disabled={saving} className="btn-primary inline-flex items-center gap-2" data-testid="meta-save"><FloppyDisk size={16} /> {saving ? "Saving..." : "Save Meta"}</button>
        <button onClick={sendTestEvent} disabled={testing} type="button" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-[#DCE5D4] text-sm font-semibold hover:bg-[#EEF4E9]" data-testid="meta-test">{testing ? "Sending..." : "Send Test Event"}</button>
      </div>
    </Section>
  );
}

const DOCUMENT_FIELDS = [
  { key: "darpan", label: "Darpan Registration Certificate" },
  { key: "pan", label: "PAN Card" },
  { key: "reg_80g", label: "80G Certificate" },
];

function DocumentsSection() {
  const [docs, setDocs] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/admin/settings/documents").then((r) => setDocs(r.data)); }, []);
  if (!docs) return null;
  const patch = (key, field, value) => setDocs((d) => ({ ...d, [key]: { ...d[key], [field]: value } }));
  const upload = async (key, file) => {
    try {
      const fd = new FormData(); fd.append("file", file);
      const up = await api.post("/admin/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDocs((d) => ({ ...d, [key]: { url: up.data.url, filename: file.name } }));
      toast.success("Document uploaded");
    } catch { toast.error("Upload failed"); }
  };
  const save = async () => {
    setSaving(true);
    try { await api.post("/admin/settings/documents", docs); toast.success("Documents saved"); }
    catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };
  return (
    <Section title="NGO Verification Documents" description="Upload the Darpan Registration Certificate, PAN, and 80G Certificate so donors can verify the organisation is authentic. This page is not linked anywhere — share the URL directly with donors who ask.">
      <div className="grid md:grid-cols-2 gap-4">
        {DOCUMENT_FIELDS.map(({ key, label }) => (
          <Field label={label} key={key}>
            <div className="flex items-center gap-3">
              {docs[key]?.url && (
                <a href={docs[key].url} target="_blank" rel="noreferrer" className="text-sm text-[#2F7A4A] underline truncate max-w-[10rem]">
                  {docs[key].filename || "View"}
                </a>
              )}
              <label className="btn-secondary cursor-pointer !py-2 !px-4 text-sm" data-testid={`documents-${key}-upload`}>
                <input type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => e.target.files[0] && upload(key, e.target.files[0])} />
                {docs[key]?.url ? "Replace" : "Upload"}
              </label>
              {docs[key]?.url && (
                <button type="button" onClick={() => setDocs((d) => ({ ...d, [key]: { url: "", filename: "" } }))} className="text-sm text-[#B84A34]">Remove</button>
              )}
            </div>
          </Field>
        ))}
      </div>
      <button onClick={save} disabled={saving} className="btn-primary inline-flex items-center gap-2" data-testid="documents-save">
        <FloppyDisk size={16} /> {saving ? "Saving..." : "Save Documents"}
      </button>
    </Section>
  );
}

function PasswordSection() {
  const [oldP, setOld] = useState("");
  const [newP, setNew] = useState("");
  const [confirmP, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (newP !== confirmP) return toast.error("Passwords do not match");
    if (newP.length < 8) return toast.error("Password must be at least 8 characters");
    setSaving(true);
    try {
      await api.post("/auth/change-password", { old_password: oldP, new_password: newP });
      toast.success("Password changed");
      setOld(""); setNew(""); setConfirm("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };
  return (
    <Section title="Change Admin Password">
      <div className="grid md:grid-cols-3 gap-4">
        <Field label="Current password">
          <input type="password" value={oldP} onChange={(e) => setOld(e.target.value)} className="w-full px-4 py-2.5 rounded-xl border border-[#DCE5D4] bg-white" data-testid="change-pw-old" />
        </Field>
        <Field label="New password">
          <input type="password" value={newP} onChange={(e) => setNew(e.target.value)} className="w-full px-4 py-2.5 rounded-xl border border-[#DCE5D4] bg-white" data-testid="change-pw-new" />
        </Field>
        <Field label="Confirm">
          <input type="password" value={confirmP} onChange={(e) => setConfirm(e.target.value)} className="w-full px-4 py-2.5 rounded-xl border border-[#DCE5D4] bg-white" data-testid="change-pw-confirm" />
        </Field>
      </div>
      <button onClick={submit} disabled={saving} className="btn-primary inline-flex items-center gap-2" data-testid="change-pw-submit"><Lock size={16} /> {saving ? "Updating..." : "Update Password"}</button>
    </Section>
  );
}

function EmailChangeSection() {
  const [newEmail, setNewEmail] = useState("");
  const [pw, setPw] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!newEmail || !pw) return toast.error("Enter new email and current password");
    setSaving(true);
    try {
      const r = await api.post("/auth/change-email", { new_email: newEmail, current_password: pw });
      toast.success(r.data.message || "Email updated");
      // Token is tied to old email — force relogin
      localStorage.removeItem("seva_admin_token");
      setTimeout(() => { window.location.href = `${process.env.PUBLIC_URL}/admin/login`; }, 1200);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };
  return (
    <Section title="Change Admin Email" description="Updating the login email requires your current password. You'll be signed out and need to log in again with the new email.">
      <div className="grid md:grid-cols-2 gap-4">
        <Field label="New admin email">
          <input type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} placeholder="name@seva-animals.org" className="w-full px-4 py-2.5 rounded-xl border border-[#DCE5D4] bg-white" data-testid="change-email-new" />
        </Field>
        <Field label="Current password">
          <input type="password" value={pw} onChange={(e) => setPw(e.target.value)} className="w-full px-4 py-2.5 rounded-xl border border-[#DCE5D4] bg-white" data-testid="change-email-pw" />
        </Field>
      </div>
      <button onClick={submit} disabled={saving} className="btn-primary inline-flex items-center gap-2" data-testid="change-email-submit"><Lock size={16} /> {saving ? "Updating..." : "Update Email"}</button>
    </Section>
  );
}

function AisensySection() {
  const [cfg, setCfg] = useState({});
  const [saving, setSaving] = useState(false);
  const [testPhone, setTestPhone] = useState("");
  useEffect(() => { api.get("/admin/settings/aisensy").then((r) => setCfg(r.data || {})); }, []);
  const patch = (k, v) => setCfg((c) => ({ ...c, [k]: v }));
  const save = async () => {
    setSaving(true);
    try {
      await api.post("/admin/settings/aisensy", {
        enabled: !!cfg.enabled, api_key: cfg.api_key || "", success_campaign: cfg.success_campaign || "",
        failed_campaign: cfg.failed_campaign || "", receipt_attach: cfg.receipt_attach !== false,
      });
      toast.success("WhatsApp settings saved");
    } catch { toast.error("Save failed"); } finally { setSaving(false); }
  };
  const test = async () => {
    if (!testPhone) return toast.error("Enter a phone number to test");
    try {
      await api.post("/admin/settings/aisensy/test", { phone: testPhone, name: "Test Donor" });
      toast.success("Test WhatsApp sent — check your phone!");
    } catch (e) { toast.error(e?.response?.data?.detail || "Test failed"); }
  };
  return (
    <Section title="WhatsApp Notifications (AiSensy)" description="Send donors an automated WhatsApp message with their receipt after a successful donation. Requires an AiSensy account with a pre-approved WhatsApp template and an API Campaign.">
      <div className="flex items-center justify-between gap-3">
        <div className="text-sm">Enable WhatsApp notifications</div>
        <Toggle checked={!!cfg.enabled} onChange={(v) => patch("enabled", v)} testid="aisensy-enable" />
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <Field label="AiSensy API Key" hint="Copy from AiSensy → Manage → API Key. Keep this private.">
          <input type="password" value={cfg.api_key || ""} onChange={(e) => patch("api_key", e.target.value)} className="w-full px-4 py-2.5 rounded-xl border border-[#DCE5D4] bg-white font-mono text-xs" data-testid="aisensy-api-key" />
        </Field>
        <Field label="Success Campaign Name" hint='The name of the "API Campaign" you created in AiSensy for successful donations.'>
          <input value={cfg.success_campaign || ""} onChange={(e) => patch("success_campaign", e.target.value)} placeholder="donation_success" className="w-full px-4 py-2.5 rounded-xl border border-[#DCE5D4] bg-white" data-testid="aisensy-success-campaign" />
        </Field>
        <Field label="Failed Campaign Name (optional)" hint="Sent when a donor's payment fails or is abandoned.">
          <input value={cfg.failed_campaign || ""} onChange={(e) => patch("failed_campaign", e.target.value)} placeholder="donation_failed" className="w-full px-4 py-2.5 rounded-xl border border-[#DCE5D4] bg-white" data-testid="aisensy-failed-campaign" />
        </Field>
        <Field label="Template parameters hint" hint="Your success template should accept 4 placeholders in this order: {{1}}=Name, {{2}}=Amount, {{3}}=Campaign, {{4}}=Receipt No. For the document header, use the media URL we send automatically.">
          <div className="w-full px-4 py-2.5 rounded-xl bg-[#F5F8F2] border border-[#DCE5D4] text-xs text-[#56624F] font-mono">{"{{1}} · {{2}} · {{3}} · {{4}}"}</div>
        </Field>
      </div>
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={cfg.receipt_attach !== false} onChange={(e) => patch("receipt_attach", e.target.checked)} data-testid="aisensy-receipt-attach" />
        Attach PDF receipt to the WhatsApp success message
      </label>
      <div className="pt-2 border-t border-[#DCE5D4] flex flex-wrap items-center gap-3">
        <button onClick={save} disabled={saving} className="btn-primary inline-flex items-center gap-2" data-testid="aisensy-save"><FloppyDisk size={16} /> {saving ? "Saving..." : "Save WhatsApp"}</button>
        <div className="flex items-center gap-2">
          <input value={testPhone} onChange={(e) => setTestPhone(e.target.value)} placeholder="+91 9XXXXXXXXX" className="px-3 py-2 rounded-xl border border-[#DCE5D4] bg-white text-sm" data-testid="aisensy-test-phone" />
          <button onClick={test} className="btn-secondary inline-flex items-center gap-2 text-sm" data-testid="aisensy-test-btn"><TestTube size={16} /> Send Test</button>
        </div>
      </div>
    </Section>
  );
}

function Fast2SmsSection() {
  const [cfg, setCfg] = useState({});
  const [saving, setSaving] = useState(false);
  const [testPhone, setTestPhone] = useState("");
  useEffect(() => { api.get("/admin/settings/fast2sms").then((r) => setCfg(r.data || {})); }, []);
  const patch = (k, v) => setCfg((c) => ({ ...c, [k]: v }));
  const save = async () => {
    setSaving(true);
    try {
      await api.post("/admin/settings/fast2sms", {
        enabled: !!cfg.enabled, api_key: cfg.api_key || "",
        sender_id: cfg.sender_id || "", route: cfg.route || "dlt",
        success_template_id: cfg.success_template_id || "",
        success_var_count: Number(cfg.success_var_count) || 1,
        failed_template_id: cfg.failed_template_id || "",
        failed_var_count: Number(cfg.failed_var_count) || 1,
      });
      toast.success("SMS settings saved");
    } catch { toast.error("Save failed"); } finally { setSaving(false); }
  };
  const test = async () => {
    if (!testPhone) return toast.error("Enter a phone number to test");
    try {
      await api.post("/admin/settings/fast2sms/test", { phone: testPhone, name: "Test" });
      toast.success("Test SMS sent — check your phone!");
    } catch (e) { toast.error(e?.response?.data?.detail || "Test failed"); }
  };
  return (
    <Section title="SMS Notifications (Fast2SMS)" description="Send donors an SMS alert for successful and failed donations using Fast2SMS DLT templates. Get your API key from Fast2SMS Dashboard → Dev API.">
      <div className="flex items-center justify-between gap-3">
        <div className="text-sm">Enable SMS notifications</div>
        <Toggle checked={!!cfg.enabled} onChange={(v) => patch("enabled", v)} testid="fast2sms-enable" />
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <Field label="Fast2SMS API Key">
          <input type="password" value={cfg.api_key || ""} onChange={(e) => patch("api_key", e.target.value)} className="w-full px-4 py-2.5 rounded-xl border border-[#DCE5D4] bg-white font-mono text-xs" data-testid="fast2sms-api-key" />
        </Field>
        <Field label="Sender ID (DLT Header)" hint="6-character approved header (e.g. SEVATR). Optional if Fast2SMS auto-picks.">
          <input value={cfg.sender_id || ""} onChange={(e) => patch("sender_id", e.target.value.toUpperCase())} maxLength={6} className="w-full px-4 py-2.5 rounded-xl border border-[#DCE5D4] bg-white uppercase" data-testid="fast2sms-sender-id" />
        </Field>
        <Field label="Route">
          <select value={cfg.route || "dlt"} onChange={(e) => patch("route", e.target.value)} className="w-full px-4 py-2.5 rounded-xl border border-[#DCE5D4] bg-white" data-testid="fast2sms-route">
            <option value="dlt">DLT (Transactional — recommended)</option>
            <option value="q">Quick / Promotional</option>
            <option value="otp">OTP</option>
          </select>
        </Field>
        <Field label="Success DLT Template ID" hint='The numeric Template ID from Fast2SMS Dashboard → DLT Manager → Content Template (e.g. 197720).'>
          <input value={cfg.success_template_id || ""} onChange={(e) => patch("success_template_id", e.target.value)} placeholder="e.g. 197720" className="w-full px-4 py-2.5 rounded-xl border border-[#DCE5D4] bg-white" data-testid="fast2sms-success-template" />
        </Field>
        <Field label="Success template — Variable count" hint="Count the {#var#} placeholders in your approved success template. Fast2SMS rejects the SMS silently if this doesn't match (the operator drops it). Values are filled in this order: 1=Amount, 2=Name, 3=Receipt No.">
          <select value={cfg.success_var_count || 1} onChange={(e) => patch("success_var_count", Number(e.target.value))} className="w-full px-4 py-2.5 rounded-xl border border-[#DCE5D4] bg-white" data-testid="fast2sms-success-var-count">
            <option value={0}>0 — static template, no variables</option>
            <option value={1}>1 — just Amount</option>
            <option value={2}>2 — Amount, Name</option>
            <option value={3}>3 — Amount, Name, Receipt No</option>
          </select>
        </Field>
        <Field label="Failed DLT Template ID (optional)" hint="Sent when donation fails or is cancelled.">
          <input value={cfg.failed_template_id || ""} onChange={(e) => patch("failed_template_id", e.target.value)} placeholder="e.g. 197721" className="w-full px-4 py-2.5 rounded-xl border border-[#DCE5D4] bg-white" data-testid="fast2sms-failed-template" />
        </Field>
        <Field label="Failed template — Variable count">
          <select value={cfg.failed_var_count || 1} onChange={(e) => patch("failed_var_count", Number(e.target.value))} className="w-full px-4 py-2.5 rounded-xl border border-[#DCE5D4] bg-white" data-testid="fast2sms-failed-var-count">
            <option value={0}>0 — static template</option>
            <option value={1}>1 — just Amount</option>
            <option value={2}>2 — Amount, Name</option>
          </select>
        </Field>
      </div>
      <div className="pt-2 border-t border-[#DCE5D4] flex flex-wrap items-center gap-3">
        <button onClick={save} disabled={saving} className="btn-primary inline-flex items-center gap-2" data-testid="fast2sms-save"><FloppyDisk size={16} /> {saving ? "Saving..." : "Save SMS"}</button>
        <div className="flex items-center gap-2">
          <input value={testPhone} onChange={(e) => setTestPhone(e.target.value)} placeholder="+91 9XXXXXXXXX" className="px-3 py-2 rounded-xl border border-[#DCE5D4] bg-white text-sm" data-testid="fast2sms-test-phone" />
          <button onClick={test} className="btn-secondary inline-flex items-center gap-2 text-sm" data-testid="fast2sms-test-btn"><TestTube size={16} /> Send Test</button>
        </div>
      </div>
    </Section>
  );
}

export default function AdminSettings() {
  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display font-bold text-3xl md:text-4xl">Settings</h1>
        <p className="text-[#56624F] mt-1">Gateways, SMTP, Meta Pixel, WhatsApp, SMS, Branding &amp; admin security</p>
      </div>
      <BrandingSection />
      <GatewaySection name="razorpay" title="Razorpay" />
      <GatewaySection name="cashfree" title="Cashfree" />
      <SMTPSection />
      <AisensySection />
      <Fast2SmsSection />
      <MetaSection />
      <DocumentsSection />
      <EmailChangeSection />
      <PasswordSection />
    </div>
  );
}
